/*
 * UI/UX改善: 数値インパクトセクション
 * カウントアップアニメーションで実績数値を表示し、即座に信頼感を与える
 * AEO強化: 統計データ・数値の充実（AI検索で30-40%高い可視性）
 * Mobile: アイコン・フォントサイズ・余白最適化
 */

import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Building2, TrendingUp, Presentation, MapPin } from "lucide-react";

interface StatItem {
  icon: React.ElementType;
  value: number;
  suffix: string;
  label: string;
  detail: string;
}

const STATS: StatItem[] = [
  { icon: Building2, value: 50, suffix: "+", label: "支援企業数", detail: "製造業・卸売・物流・観光業など" },
  { icon: TrendingUp, value: 40, suffix: "%", label: "平均業務効率化率", detail: "導入企業の平均改善効果" },
  { icon: Presentation, value: 30, suffix: "+", label: "セミナー開催実績", detail: "広島・中四国を中心に全国で" },
  { icon: MapPin, value: 9, suffix: "県", label: "中四国対応エリア", detail: "広島・岡山・山口・島根・鳥取・香川・愛媛・徳島・高知" },
];

function useCountUp(end: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, start]);

  return count;
}

function StatCard({ stat, index }: { stat: StatItem; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const count = useCountUp(stat.value, 2000, isInView);
  const Icon = stat.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="flex flex-col items-center text-center group"
    >
      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white border border-[#E0E0E0] rounded-sm mb-3 sm:mb-4 group-hover:border-[#E03030] transition-colors duration-300">
        <Icon size={18} strokeWidth={1.5} className="text-[#E03030] sm:hidden" />
        <Icon size={22} strokeWidth={1.5} className="text-[#E03030] hidden sm:block" />
      </div>
      <div className="flex items-baseline gap-0.5 mb-0.5 sm:mb-1">
        <span
          className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1A1A1A]"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {count}
        </span>
        <span
          className="text-base sm:text-lg lg:text-xl font-bold text-[#C72828]"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {stat.suffix}
        </span>
      </div>
      <span className="text-[11px] sm:text-xs font-bold text-[#1A1A1A] mb-0.5">{stat.label}</span>
      <span className="text-[9px] sm:text-[10px] text-[#666666] leading-tight max-w-[120px] sm:max-w-[160px]">{stat.detail}</span>
    </motion.div>
  );
}

export function StatsSection() {
  return (
    <section className="py-8 sm:py-12 lg:py-16 bg-[#F9F9F9] border-b border-[#E0E0E0]">
      <div className="container">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 lg:gap-12 max-w-3xl mx-auto">
          {STATS.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
